import React, { useState, useEffect } from 'react';
import {
  X, Pencil, Power, Shield, Mail, Clock, Calendar,
  User as UserIcon, KeyRound, Users, Layers, AlertCircle,
  Fingerprint, Sparkles
} from 'lucide-react';
import type { User } from '../../services/users/users.service';
import { RolePermissionsEditor } from '../security/RolePermissionsEditor';
import { ConfirmationToast } from '../ui/ConfirmationToast';
import { NotificationToast } from '../ui/NotificationToast';
import { usePermissions } from '../../hooks/usePermissions';

interface UserDetailsDrawerProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onEditUser: (user: User) => void;
  onToggleUserStatus: (user: User) => Promise<void>;
  onViewMember?: (user: User) => void;
}

export const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({
  isOpen,
  user,
  onClose,
  onEditUser,
  onToggleUserStatus,
  onViewMember
}) => {
  const { hasPermission } = usePermissions();
  const [isVisible, setIsVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'roles'>('info');

  const canEditUsers = hasPermission('Usuarios', 'editar');
  const canAdministerUsers = hasPermission('Usuarios', 'administrar');
  const canActivateUsers = hasPermission('Usuarios', 'activar');
  const canDeactivateUsers = hasPermission('Usuarios', 'desactivar');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<'toggle' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      setCurrentUser(user);
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => setCurrentUser(null), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTipoUsuarioConfig = (tipo: string) => {
    switch (tipo) {
      case 'administrador':
        return { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: <Shield className="w-3.5 h-3.5" />, label: 'Administrador' };
      case 'miembro':
        return { color: 'bg-green-50 text-green-700 border-green-200', icon: <Users className="w-3.5 h-3.5" />, label: 'Miembro' };
      default:
        return { color: 'bg-gray-50 text-gray-700 border-gray-200', icon: <UserIcon className="w-3.5 h-3.5" />, label: tipo };
    }
  };

  const handleEditUser = () => {
    if (currentUser) onEditUser(currentUser);
  };

  const handleToggleUserStatus = () => {
    setConfirmationAction('toggle');
    setShowConfirmation(true);
  };

  const handleConfirmAction = async () => {
    if (!currentUser || !confirmationAction) return;
    setIsLoading(true);
    try {
      if (confirmationAction === 'toggle') {
        await onToggleUserStatus(currentUser);
        setNotificationConfig({
          title: '¡Éxito!',
          message: `Usuario ${currentUser.estado ? 'desactivado' : 'activado'} correctamente`,
          type: 'success'
        });
        setShowNotification(true);
      }
    } catch (error) {
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudo cambiar el estado del usuario. Inténtalo nuevamente.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const getConfirmationConfig = () => {
    if (!currentUser || !confirmationAction) return null;
    if (confirmationAction === 'toggle') {
      return {
        title: currentUser.estado ? 'Desactivar Usuario' : 'Activar Usuario',
        message: currentUser.estado
          ? `¿Estás seguro de que deseas desactivar a ${currentUser.nombreCompleto}? El usuario no podrá acceder al sistema.`
          : `¿Estás seguro de que deseas activar a ${currentUser.nombreCompleto}? El usuario podrá acceder al sistema nuevamente.`,
        confirmText: currentUser.estado ? 'Desactivar' : 'Activar',
        type: currentUser.estado ? 'danger' as const : 'success' as const
      };
    }
    return null;
  };

  if (!isOpen && !currentUser) return null;
  if (!currentUser) return null;

  const tipoConfig = getTipoUsuarioConfig(currentUser.tipoUsuario);
  const initials = `${currentUser.nombreCompleto.charAt(0).toUpperCase()}${currentUser.nombreCompleto.split(' ')[1]?.charAt(0)?.toUpperCase() || ''}`;
  const activeRole = currentUser.roles?.find(r => r.activo);

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay */}
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={handleClose}
        />

        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="h-full flex flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>

            {/* ═══ HEADER CON AVATAR INTEGRADO ═══ */}
            <div className="px-6 py-5 bg-white border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-200">
                    <span className="text-white text-xs font-medium">{initials}</span>
                  </div>
                  <div>
                    <h2 className="text-xs font-medium text-gray-900 leading-tight">{currentUser.nombreCompleto}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" /> {currentUser.email}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={handleClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Badges de estado */}
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {/* Rol — principal */}
                {currentUser.roles && currentUser.roles.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 border border-purple-300">
                    <Shield className="w-3.5 h-3.5" />
                    {(currentUser.roles.find(r => r.activo) || currentUser.roles[0])?.nombre}
                  </span>
                )}
                {/* Tipo de usuario — secundario */}
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-md border ${tipoConfig.color}`}>
                  {tipoConfig.icon} {tipoConfig.label}
                </span>
                {currentUser.estado ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-green-50 text-green-700 border border-green-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-red-50 text-red-700 border border-red-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Inactivo
                  </span>
                )}
                {currentUser.requiereCambioContrasena && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                    <KeyRound className="w-3 h-3" /> Cambio de clave requerido
                  </span>
                )}
              </div>
            </div>

            {/* ═══ TABS ═══ */}
            <div className="px-6 border-b border-gray-100 bg-gray-50/50">
              <div className="flex gap-1">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-4 py-3 text-xs font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'info'
                      ? 'text-purple-700 border-purple-500'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <UserIcon className="w-4 h-4" /> Información General
                </button>
                <button
                  onClick={() => setActiveTab('roles')}
                  className={`px-4 py-3 text-xs font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'roles'
                      ? 'text-purple-700 border-purple-500'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Shield className="w-4 h-4" /> Roles y Permisos
                  {currentUser.roles?.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full font-medium">
                      {currentUser.roles.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* ═══ CONTENT ═══ */}
            <div className="flex-1 overflow-y-auto bg-gray-50/30">
              <div className="p-6">
                {activeTab === 'info' && (
                  <div className="space-y-5">
                    {/* Información básica — Card */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-purple-600" />
                        <h4 className="text-xs font-medium text-gray-900">Información Básica</h4>
                      </div>
                      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <InfoField icon={<Fingerprint className="w-4 h-4" />} label="ID de Usuario" value={currentUser.id} mono />
                        <InfoField icon={<Shield className="w-4 h-4" />} label="Tipo de Usuario" value={currentUser.tipoUsuario} capitalize />
                        <InfoField icon={<Sparkles className="w-4 h-4" />} label="Estado" value={currentUser.estado ? 'Activo' : 'Inactivo'} status={currentUser.estado ? 'success' : 'danger'} />
                        <InfoField icon={<KeyRound className="w-4 h-4" />} label="Cambio de Clave Requerido" value={currentUser.requiereCambioContrasena ? 'Sí' : 'No'} status={currentUser.requiereCambioContrasena ? 'warning' : 'success'} />
                        <InfoField icon={<Clock className="w-4 h-4" />} label="Último Acceso" value={currentUser.ultimoAcceso ? formatDate(currentUser.ultimoAcceso) : 'Nunca'} />
                      </div>
                    </div>

                    {/* Membresía — Card */}
                    {currentUser.membresia && (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-green-50/60 border-b border-green-100 flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-600" />
                          <h4 className="text-xs font-medium text-gray-900">Información de Membresía</h4>
                        </div>
                        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                          <InfoField label="Nombres" value={`${currentUser.membresia.nombres} ${currentUser.membresia.apellidos}`} />
                          <InfoField label="Teléfono" value={currentUser.membresia.telefono} />
                          {currentUser.membresia.numeroDocumento && (
                            <InfoField label="Documento" value={currentUser.membresia.numeroDocumento} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sistema — Card compacta */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <h4 className="text-xs font-medium text-gray-900">Registro en el Sistema</h4>
                      </div>
                      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <InfoField icon={<Calendar className="w-4 h-4" />} label="Creado" value={formatDate(currentUser.creadoEn)} />
                        <InfoField icon={<Clock className="w-4 h-4" />} label="Última Actualización" value={formatDate(currentUser.actualizadoEn)} />
                        {currentUser.membresiaId && (
                          <InfoField icon={<Fingerprint className="w-4 h-4" />} label="ID Membresía" value={currentUser.membresiaId} mono />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'roles' && (
                  <div className="space-y-5">
                    {activeRole ? (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-900 flex items-center gap-2">
                            <Layers className="w-4 h-4 text-purple-600" />
                            Permisos del rol "{activeRole.nombre}"
                          </h4>
                        </div>
                        <div className="p-5">
                          <p className="text-[11px] text-gray-400 mb-4">
                            Estos permisos pertenecen al rol y aplican a todos los usuarios que
                            lo tengan asignado.
                          </p>
                          <RolePermissionsEditor
                            roleId={activeRole.id}
                            readOnly={!hasPermission('Roles', 'administrar')}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-16 bg-white rounded-xl border border-gray-200 border-dashed">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                          <Shield className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Sin roles asignados</p>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto">Este usuario no tiene roles configurados. Contacta al administrador para asignar permisos.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ═══ FOOTER DE ACCIONES ═══ */}
            <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                ID: <span className="font-mono text-gray-500">{currentUser.id.slice(0, 8)}...</span>
              </span>
              <div className="flex gap-3">
                {currentUser.membresiaId && onViewMember && (
                  <button
                    onClick={() => onViewMember(currentUser)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
                  >
                    <UserIcon className="w-4 h-4" />
                    Ver miembro
                  </button>
                )}
                {((currentUser.estado && canDeactivateUsers) || (!currentUser.estado && canActivateUsers) || canAdministerUsers) && (
                  <button
                    onClick={handleToggleUserStatus}
                    disabled={isLoading}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                      currentUser.estado
                        ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                        : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                    }`}
                  >
                    <Power className="w-4 h-4" />
                    {currentUser.estado ? 'Desactivar' : 'Activar'}
                  </button>
                )}
                {canEditUsers && (
                  <button
                    onClick={handleEditUser}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm shadow-purple-200"
                  >
                    <Pencil className="w-4 h-4" />
                    Editar Usuario
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Confirmation Toast */}
      {(() => {
        const config = getConfirmationConfig();
        return config ? (
          <ConfirmationToast
            isOpen={showConfirmation}
            title={config.title}
            message={config.message}
            confirmText={config.confirmText}
            type={config.type}
            onConfirm={handleConfirmAction}
            onCancel={handleCancelAction}
            isLoading={isLoading}
          />
        ) : null;
      })()}

      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => { setShowNotification(false); setNotificationConfig(null); }}
          duration={7000}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   COMPONENTE AUXILIAR: InfoField
   ═══════════════════════════════════════════ */
interface InfoFieldProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
  capitalize?: boolean;
  status?: 'success' | 'danger' | 'warning' | 'neutral';
}

const InfoField: React.FC<InfoFieldProps> = ({ icon, label, value, mono, capitalize, status }) => {
  const statusDot = status && (
    <span className={`w-2 h-2 rounded-full ${
      status === 'success' ? 'bg-green-500' :
      status === 'danger' ? 'bg-red-500' :
      status === 'warning' ? 'bg-amber-500' : 'bg-gray-400'
    }`} />
  );

  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
        {icon} {label}
      </label>
      <div className="flex items-center gap-2">
        {statusDot}
        <p className={`text-xs font-medium text-gray-900 ${mono ? 'font-mono text-xs' : ''} ${capitalize ? 'capitalize' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  );
};

export default UserDetailsDrawer;
